import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link } from '@material-ui/core';
import React, { forwardRef, Ref, useImperativeHandle, useMemo, useRef, useState } from 'react';

type ButtonLinkProps = {
  href: string;
  target?: string;
};

type DialogButton = {
  text: string;
  onClick?: () => void;
  dataCy?: string;
  link?: ButtonLinkProps;
};

type DialogOptions = {
  title?: string;
  message?: string;
  buttons?: DialogButton[];
};

type Props = {};

export type DialogRefAttrs = {
  alert: (options: DialogOptions) => void;
  choose: (options: DialogOptions) => Promise<DialogButton | null>;
};

type ResolverFunction = (value: DialogButton | null) => void;

function DialogAlert(_: Props, ref: Ref<DialogRefAttrs>) {
  const [alertOptions, setAlertOptions] = useState<DialogOptions | null>(null);

  const choosePromiseResolver = useRef<ResolverFunction | null>(null);

  function handleChoose(options: DialogOptions): Promise<DialogButton | null> {
    setAlertOptions(options);
    return new Promise<DialogButton | null>((resolve) => {
      choosePromiseResolver.current = resolve;
    });
  }

  useImperativeHandle(ref, () => {
    return {
      alert: (options: DialogOptions) => setAlertOptions(options),
      choose: handleChoose,
    };
  });

  function handleButtonClick(button: DialogButton) {
    const isToChooseButton = !!choosePromiseResolver.current;
    if (isToChooseButton) {
      choosePromiseResolver.current && choosePromiseResolver.current(button);
      choosePromiseResolver.current = null;
    } else {
      button.onClick && button.onClick();
    }

    setAlertOptions(null);
  }

  function handleOnClose() {
    choosePromiseResolver.current && choosePromiseResolver.current(null);
    choosePromiseResolver.current = null;
    setAlertOptions(null);
  }

  const shouldUseOkDefaultAction = Boolean(!alertOptions?.buttons || alertOptions.buttons.length === 0);

  return (
    <Dialog open={Boolean(alertOptions)} onClose={handleOnClose}>
      <DialogTitle>{alertOptions?.title ?? ''}</DialogTitle>
      <DialogContent>
        <DialogContentText>{alertOptions?.message ?? ''}</DialogContentText>
      </DialogContent>
      <DialogActions>
        {shouldUseOkDefaultAction && (
          <Button
            onClick={() => handleButtonClick({ text: 'OK', onClick: () => setAlertOptions(null) })}
            data-cy="dialog-alert-ok-btn"
          >
            OK
          </Button>
        )}
        {(alertOptions?.buttons?.length ?? 0) > 0 && (
          <>
            {alertOptions?.buttons?.map((button, index) => {
              if (button.link) {
                return (
                  <Link href={button.link.href} target={button.link.target}>
                    <Button key={String(index)} onClick={() => handleButtonClick(button)} data-cy={button.dataCy}>
                      {button.text}
                    </Button>
                  </Link>
                );
              }

              return (
                <Button key={String(index)} onClick={() => handleButtonClick(button)} data-cy={button.dataCy}>
                  {button.text}
                </Button>
              );
            })}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

const DialogAlertForwaredRef = forwardRef<DialogRefAttrs, Props>(DialogAlert);

export function useDialogAlert() {
  const dialogRef = useRef<DialogRefAttrs>(null);

  const DialogAlert = useMemo(() => {
    return <DialogAlertForwaredRef ref={dialogRef} />;
  }, []);

  return { dialogRef: dialogRef, DialogAlert };
}

export default DialogAlertForwaredRef;
